import React from "react";
import { GhlInboxData } from "../types";

interface InboxCardProps {
  data: GhlInboxData;
}

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  return d.toLocaleString(undefined, {
    month: isToday ? undefined : "short",
    day: isToday ? undefined : "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const headerIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-secondary) 20%, transparent)",
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("") || "?";

const avatarTint = (i: number): React.CSSProperties => {
  const variants: React.CSSProperties[] = [
    { background: "color-mix(in srgb, var(--color-primary) 16%, transparent)", color: "var(--color-primary)" },
    { background: "color-mix(in srgb, var(--color-secondary) 18%, transparent)", color: "var(--color-primary)" },
    {
      background: "color-mix(in srgb, var(--color-accent-warm) 24%, transparent)",
      color: "color-mix(in srgb, var(--color-accent-warm) 55%, var(--color-text-primary) 45%)",
    },
  ];
  return variants[i % variants.length];
};

export const InboxCard: React.FC<InboxCardProps> = ({ data }) => {
  const { conversations } = data;
  const unreadCount = conversations.filter(c => c.unread).length;

  return (
    <div className="my-1 w-full max-w-full overflow-hidden rounded-2xl border border-theme-border bg-theme-surface">
      <div className="flex items-center justify-between border-b border-theme-border px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl" style={headerIconStyle}>
            <span className="material-icons-outlined text-[18px] text-theme-primary">inbox</span>
          </span>
          <p className="text-[13px] font-bold text-theme-fg">
            Inbox ({conversations.length})
          </p>
        </div>
        {unreadCount > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold bg-theme-primary/10 text-theme-primary">
            {unreadCount} unread
          </span>
        )}
      </div>

      <div className="flex max-h-[400px] flex-col overflow-y-auto divide-y divide-theme-border">
        {conversations.length === 0 && (
          <p className="py-6 text-center text-[12.5px] text-theme-fg-muted">
            No conversations found.
          </p>
        )}
        {conversations.map((c, i) => (
          <div
            key={c.conversation_id}
            className={`flex items-start gap-3 p-3 transition-colors hover:bg-theme-background/50 ${
              c.unread ? 'bg-theme-primary/5' : ''
            }`}
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold relative" style={avatarTint(i)}>
              {initials(c.contact_name)}
              {c.unread && (
                <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-theme-surface bg-theme-primary"></span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-[13px] truncate ${c.unread ? 'font-bold text-theme-fg' : 'font-medium text-theme-fg/90'}`}>
                  {c.contact_name || "(no name)"}
                </p>
                <span className="text-[10.5px] whitespace-nowrap text-theme-fg-muted flex-shrink-0">
                  {formatDate(c.last_message_date)}
                </span>
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="material-icons-outlined text-[11px] text-theme-fg-muted opacity-80 shrink-0">
                  {c.message_type?.toLowerCase().includes('email') ? 'mail' : 'sms'}
                </span>
                <p className={`text-[11.5px] truncate ${c.unread ? 'text-theme-fg font-medium' : 'text-theme-fg-muted'}`}>
                  {c.last_message_preview}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
